import React, { useCallback, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import AiGenerativeTextIcon from '@atlaskit/icon-lab/core/ai-generative-text';

import { Ati } from '@atlassian/ari';

import { getApolloClient } from '@confluence/graphql/entry-points/getApolloClient';
import { useSmartCreationModalActions } from '@confluence/smart-create/entry-points/smartCreationModal';
import { AISmartCreateSpotlight } from '@confluence/onboarding-ai-first-create/entry-points/AISmartCreateSpotlight';

import type { UseMenuItemProps, UseMenuItemReturnProps } from '.';

import { useUniversalCreateActions, useUniversalCreateState } from '../useUniversalCreate';
import { ItemTitleWithLozenge } from '../ItemTitle';
import type {
	GetPromotedSpacesQueryVariables,
	GetPromotedSpacesQuery as GetPromotedSpacesQueryType,
} from '../../queries/__types__/GetPromotedSpacesQuery';
import { GetPromotedSpacesQuery } from '../../queries/GetPromotedSpacesQuery.experimentalgraphql';

const i18n = defineMessages({
	smartCreateTitle: {
		id: 'create-blank-fabric-page.create.content.smart-create-with-ai.title',
		defaultMessage: 'Create with Rovo',
		description: 'title indicating that this option will trigger an AI guided creation workflow',
	},
});

const getSpaceKeyForCreation = async (): Promise<string> => {
	const spaceKeyRequest: GetPromotedSpacesQueryVariables = {
		promotedSpaceKey: undefined,
	};
	// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
	const spaceKeyResponse: GetPromotedSpacesQueryType = (
		await getApolloClient().query({
			query: GetPromotedSpacesQuery,
			variables: spaceKeyRequest,
		})
	).data;
	return spaceKeyResponse?.experimentalPromotedSpaces?.nodes?.[0]?.id ?? '';
};

/**
 * This is a menu item for an internal demo/spike of a new AI powered creation workflow.
 */
export function useAISmartCreateItem({
	spaceKey,
	onClick,
	source,
	openFrom,
}: UseMenuItemProps): UseMenuItemReturnProps {
	const { formatMessage } = useIntl();
	const { openModal } = useSmartCreationModalActions();
	const { setIsOpen } = useUniversalCreateActions();
	const { isOpen } = useUniversalCreateState();
	const [fetchedSpaceKey, setFetchedSpaceKey] = useState<string>('');

	// Promoted Spaces query should be cached, but we do have a race condition here for global create where there
	// can potentially be missing space key. This is fine for the early spike stage.
	useEffect(() => {
		void getSpaceKeyForCreation().then(setFetchedSpaceKey);
	}, []);

	const handleClick = useCallback(
		(actionSubjectId: string, attributes?: Record<string, string>) => {
			onClick(actionSubjectId, attributes);
			openModal(spaceKey ?? fetchedSpaceKey);
		},
		[onClick, spaceKey, fetchedSpaceKey, openModal],
	);

	return {
		ati: Ati.parse('ati:cloud:confluence:page'), // todo: this is incorrect, we don't know the type identifier yet
		children: (
			<ItemTitleWithLozenge useLabsLozenge>
				{formatMessage(i18n.smartCreateTitle)}
			</ItemTitleWithLozenge>
		),
		Wrapper: (props) => (
			<AISmartCreateSpotlightWrapper
				{...props}
				handleClick={() =>
					handleClick('createAISmartCreatePopoverItem', { spotlight: 'AISmartCreateSpotlight' })
				}
				source={source}
				openFrom={openFrom}
				setIsOpen={setIsOpen}
				isOpen={isOpen}
			/>
		),
		onClick: () => handleClick('createAISmartCreatePopoverItem'),
		elemBefore: AiGenerativeTextIcon,
		testId: `create-menu-link-item`,
	};
}

const AISmartCreateSpotlightWrapper = ({
	children,
	source,
	handleClick,
	openFrom,
	setIsOpen,
	isOpen,
}: {
	children: () => React.ReactNode;
	handleClick: () => void;
	source: string;
	openFrom: string | undefined;
	setIsOpen: (isOpen: boolean) => void;
	isOpen: boolean;
}) => {
	return (
		<AISmartCreateSpotlight
			source={openFrom ? `${source}-${openFrom}` : source}
			handleClick={handleClick}
			setIsOpen={setIsOpen}
			isOpen={isOpen}
		>
			{children()}
		</AISmartCreateSpotlight>
	);
};
